import { useCallback, useEffect, useRef, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import { formatDuration } from '@/media/format';
import { MAX_VIDEO_NOTE_MS, MAX_VOICE_MS } from '@/protocol/limits';
import styles from '@/components/media.module.css';

export type RecordingKind = 'voice' | 'video_note';

export interface VoiceRecorderProps {
  kind: RecordingKind;
  /**
   * Called the instant recording stops, before review.
   *
   * Section 14: "Persist a recorded draft immediately after stop." The caller
   * writes the upload record from here; the review step below then edits an
   * attachment that already exists on disk rather than one held in memory that
   * a closed tab would lose.
   */
  onRecorded: (blob: Blob, durationMs: number, mimeType: string) => void;
  /** The user discarded the recording during review. */
  onDiscard: () => void;
  /** The user accepted the recording. */
  onAccept: () => void;
  onCancel: () => void;
}

/**
 * Section 06 capture format order: "web MediaRecorder chooses WebM/Opus first,
 * MP4/AAC second; unsupported recording disables record UI with explanation".
 */
const VOICE_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'] as const;
const VIDEO_TYPES = ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'] as const;

function chooseMimeType(kind: RecordingKind): string | null {
  const candidates = kind === 'voice' ? VOICE_TYPES : VIDEO_TYPES;
  const recorder = globalThis.MediaRecorder as
    | (typeof MediaRecorder & { isTypeSupported?: (type: string) => boolean })
    | undefined;
  if (!recorder) {
    return null;
  }
  for (const candidate of candidates) {
    if (recorder.isTypeSupported?.(candidate) ?? false) {
      return candidate;
    }
  }
  // A recorder that supports nothing on the list can still record in its own
  // default container. Returning the empty string lets `MediaRecorder` choose,
  // and the server detects the real type from the bytes regardless.
  return '';
}

/**
 * Press to start, press to stop, visible duration, cancel, review before send.
 *
 * Permission denial is the case that matters most: Section 14 requires the text
 * composer to stay usable and the refusal to explain itself, so a denied
 * microphone renders an explanatory notice and a dismiss action, never a dead
 * control or a modal the user has to escape.
 */
export function VoiceRecorder(props: VoiceRecorderProps) {
  const { t } = useStrings();
  const [state, setState] = useState<'idle' | 'recording' | 'review' | 'denied' | 'unsupported'>(
    'idle',
  );
  const [elapsedMs, setElapsedMs] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const limitMs = props.kind === 'voice' ? MAX_VOICE_MS : MAX_VIDEO_NOTE_MS;

  const releaseStream = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) {
      track.stop();
    }
    streamRef.current = null;
  }, []);

  const clearTick = useCallback(() => {
    if (tickRef.current !== null) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTick();
      releaseStream();
      if (previewUrl !== null) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [clearTick, releaseStream, previewUrl]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return;
    }
    recorder.stop();
    clearTick();
  }, [clearTick]);

  const start = useCallback(async () => {
    const mimeType = chooseMimeType(props.kind);
    if (mimeType === null || !navigator.mediaDevices?.getUserMedia) {
      setState('unsupported');
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(
        props.kind === 'voice'
          ? // Section 06 captures voice in mono.
            { audio: { channelCount: 1 } }
          : // A video note is a square capture rendered circularly.
            { audio: true, video: { width: 480, height: 480, facingMode: 'user' } },
      );
    } catch {
      // Refused, or no device. Either way the text composer keeps working and
      // the notice says what happened.
      setState('denied');
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];
    const recorder = new MediaRecorder(stream, mimeType === '' ? undefined : { mimeType });
    recorderRef.current = recorder;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onstop = () => {
      const durationMs = Date.now() - startedAtRef.current;
      const type = recorder.mimeType || (props.kind === 'voice' ? 'audio/webm' : 'video/webm');
      const blob = new Blob(chunksRef.current, { type });
      releaseStream();
      setElapsedMs(durationMs);
      setPreviewUrl(URL.createObjectURL(blob));
      setState('review');
      // Persisted before review, per Section 14.
      props.onRecorded(blob, durationMs, type);
    };

    startedAtRef.current = Date.now();
    recorder.start();
    setElapsedMs(0);
    setState('recording');

    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      setElapsedMs(elapsed);
      if (elapsed >= limitMs) {
        // Section 06 caps a voice note at 60 minutes and a video note at 60
        // seconds. Stopping at the limit is better than letting the recording
        // run into a length the server will refuse.
        stop();
      }
    }, 200);
  }, [limitMs, props, releaseStream, stop]);

  if (state === 'unsupported' || state === 'denied') {
    return (
      <div className={styles.recorder} role="alert">
        <p className={styles.notice}>
          {state === 'unsupported'
            ? t('record.unsupported')
            : props.kind === 'voice'
              ? t('record.denied')
              : t('record.cameraDenied')}
        </p>
        <button type="button" className={styles.recordAction} onClick={props.onCancel}>
          {t('record.cancel')}
        </button>
      </div>
    );
  }

  if (state === 'review') {
    return (
      <div className={styles.recorder}>
        <span className={styles.recordTime}>
          {t('record.elapsed', { duration: formatDuration(elapsedMs) })}
        </span>
        {previewUrl === null ? null : props.kind === 'voice' ? (
          // A plain native player for review only. The full waveform player is
          // for messages that exist; this is the user checking what they just
          // said before committing to it.
          <audio className={styles.seek} controls src={previewUrl} aria-label={t('record.review')} />
        ) : (
          <video className={styles.videoNote} controls src={previewUrl} aria-label={t('record.review')} />
        )}
        <button
          type="button"
          className={`${styles.recordAction} ${styles.recordPrimary}`}
          onClick={props.onAccept}
        >
          {t('record.send')}
        </button>
        <button type="button" className={styles.recordAction} onClick={props.onDiscard}>
          {t('record.cancel')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.recorder}>
      {state === 'recording' ? (
        <>
          {/* The dot is decorative; the elapsed time beside it is the state. */}
          <span className={styles.recordDot} aria-hidden="true" />
          <span className={styles.recordTime} role="status">
            {t('record.recording')} {formatDuration(elapsedMs)}
          </span>
          <button
            type="button"
            className={`${styles.recordAction} ${styles.recordPrimary}`}
            onClick={stop}
          >
            {t('record.stop')}
          </button>
        </>
      ) : (
        <button
          type="button"
          className={`${styles.recordAction} ${styles.recordPrimary}`}
          onClick={() => {
            void start();
          }}
        >
          {t('record.start')}
        </button>
      )}
      <button
        type="button"
        className={styles.recordAction}
        onClick={() => {
          stop();
          releaseStream();
          props.onCancel();
        }}
      >
        {t('record.cancel')}
      </button>
      <span className={styles.notice}>
        {props.kind === 'voice' ? t('record.limit') : t('record.videoLimit')}
      </span>
    </div>
  );
}
